import React from 'react';
import { Link } from 'react-router-dom';
import { FaLinkedin, FaYoutube, FaFacebook, FaTwitter } from 'react-icons/fa';
import { FiMail, FiPhone } from 'react-icons/fi';
import useLanguage from '../../hooks/useLanguage';
import footerData from '../../data/footer.json';

/**
 * Footer Component
 * - Company information
 * - Quick links
 * - Social media links
 * - Copyright information
 * - Responsive: Multi-column on desktop, stacked on mobile
 */
const Footer = () => {
  const { language } = useLanguage();

  // Map icon names to actual icon components
  const iconMap = {
    FaLinkedin: FaLinkedin,
    FaYoutube: FaYoutube,
    FaFacebook: FaFacebook,
    FaTwitter: FaTwitter,
  };

  return (
    <footer className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">
              {language === 'ko' ? footerData.companyName : footerData.companyNameEn}
            </h3>
            <p className="text-gray-300 text-sm">
              {language === 'ko' ? footerData.address : footerData.addressEn}
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-gray-300">
                <FiPhone size={16} />
                <span>{footerData.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-300">
                <FiMail size={16} />
                <span>{footerData.email}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {language === 'ko' ? '바로가기' : 'Quick Links'}
            </h4>
            <ul className="space-y-2">
              {footerData.quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {language === 'ko' ? link.label : link.labelEn}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {language === 'ko' ? '정책' : 'Policies'}
            </h4>
            <ul className="space-y-2">
              {footerData.policies.map((policy, index) => (
                <li key={index}>
                  <Link
                    to={policy.path}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {language === 'ko' ? policy.label : policy.labelEn}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {language === 'ko' ? '소셜 미디어' : 'Follow Us'}
            </h4>
            <div className="flex space-x-4">
              {footerData.socialLinks.map((social, index) => {
                const IconComponent = iconMap[social.icon];
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-white transform hover:scale-110 transition-all duration-200"
                    aria-label={social.platform}
                  >
                    <IconComponent size={24} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              {language === 'ko' ? footerData.copyright : footerData.copyrightEn}
            </p>
            <div className="text-sm text-gray-400">
              <span>{footerData.businessNumberLabel}: {footerData.businessNumber}</span>
              <span className="mx-2">|</span>
              <span>{footerData.ceoLabel}: {footerData.ceo}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
